import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';

import VirtualizedTable from 'components/VirtualizedTable';

import {
    requestCreateVariety,
    requestUpdateVariety
} from 'services/varieties/actions';

import { selectVarietiesTableColumns, selectVarietiesItems } from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestCreateVariety: typeof requestCreateVariety;
    requestUpdateVariety: typeof requestUpdateVariety;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class VarietiesVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleCreate = (item: VirtualizedTableItem): void => {
        const { requestCreateVariety: createVariety } = this.props;

        createVariety(item);
    };

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const { requestUpdateVariety: updateVariety } = this.props;
        const { key, commodityId, ...payload } = item;

        updateVariety(payload);
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Variety"
                labelPlural="Varieties"
                enableItemEditing
                onItemUpdate={this.handleItemUpdate}
                enableCreating
                onCreate={this.handleCreate}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: selectVarietiesItems,
    columns: selectVarietiesTableColumns
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestCreateVariety,
    requestUpdateVariety
})(VarietiesVirtualizedTable);
